import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { ClientSidebar } from "@/components/sidebars";
import InviteFriendsModal from "@/components/InviteFriendsModal";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";

const STATUS_PILL = {
  sent: { cls: "sol-pendiente", label: "Enviada" },
  registered: { cls: "sol-completada", label: "Registrado ✓" },
};

export default function Referidos() {
  const [data, setData] = useState(null);
  const [invitations, setInvitations] = useState(null);
  const [showInvite, setShowInvite] = useState(false);

  const loadAll = () => {
    apiClient.get("/me/referrals").then(({ data }) => setData(data)).catch(() => {});
    apiClient.get("/referrals/invitations").then(({ data }) => setInvitations(data)).catch(() => {});
  };

  useEffect(() => { loadAll(); }, []);

  if (!data) return <AppLayout sidebar={ClientSidebar}><div className="panel">Cargando…</div></AppLayout>;

  const link = `${window.location.origin}/cuenta/registro?ref=${data.referral_code}`;
  const waText = encodeURIComponent(`¡Hola! Te recomiendo Mi Ángel en Casa — desde $4.99 USD/mes proteges a tu familia en México con servicio vial, ambulancia, médico y legal 24/7. Si entras con mi link, los dos ganamos 1 mes gratis: ${link}`);
  const waUrl = `https://wa.me/?text=${waText}`;

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiado al portapapeles"); };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" }) : "—";

  return (
    <AppLayout sidebar={ClientSidebar}>
      <header className="app-header">
        <h1>Programa de Referidos</h1>
        <p className="text-muted">
          ¿Sabías que tu membresía puede salirte <strong>gratis todo el año</strong>?
          Refiere 12 amigos que paguen y consigue 12 meses gratis 🎁
        </p>
      </header>

      <section className="cards-row">
        <div className="stat-card stat-ok" data-testid="stat-meses-gratis">
          <span className="stat-label">Meses gratis ganados</span>
          <strong className="stat-value">{data.free_months_earned}</strong>
          <small>1 mes por referido pagado</small>
        </div>
        <div className="stat-card" data-testid="stat-referidos-totales">
          <span className="stat-label">Referidos totales</span>
          <strong className="stat-value">{data.total_referidos}</strong>
        </div>
        <div className="stat-card stat-ok" data-testid="stat-referidos-pagados">
          <span className="stat-label">Pagados</span>
          <strong className="stat-value">{data.referidos_pagados}</strong>
        </div>
        <div className="stat-card stat-warn" data-testid="stat-referidos-pendientes">
          <span className="stat-label">Invitaciones enviadas</span>
          <strong className="stat-value">{invitations?.sent_count ?? 0}</strong>
          <small>{invitations?.registered_count ?? 0} registrados</small>
        </div>
      </section>

      {/* === CTA principal === */}
      <div className="panel referidos-cta">
        <div style={{ flex: 1 }}>
          <h2 style={{ marginTop: 0 }}>
            <i className="fas fa-paper-plane" style={{ color: "#7DC242", marginRight: 10 }} />
            Invita a tus amigos en bloque
          </h2>
          <p className="text-muted" style={{ margin: "4px 0 16px" }}>
            Importa contactos de tu directorio (Android) o envía hasta 20 invitaciones de un jalón por email.
            Nosotros mandamos un correo profesional con tu link único y un mensaje personalizable.
          </p>
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 18px", fontSize: 13.5, color: "#374151" }}>
            <li style={{ padding: "3px 0" }}>✅ Hasta <strong>50 invitaciones por día</strong></li>
            <li style={{ padding: "3px 0" }}>✅ Mensaje editable antes de enviar</li>
            <li style={{ padding: "3px 0" }}>✅ Tracking en tiempo real — ves quién se registró</li>
            <li style={{ padding: "3px 0" }}>🔒 No guardamos los contactos. Solo enviamos la invitación.</li>
          </ul>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowInvite(true)}
          data-testid="open-invite-modal"
          style={{ fontSize: 15, padding: "14px 28px", alignSelf: "flex-start" }}
        >
          <i className="fas fa-user-plus" /> Invitar amigos
        </button>
      </div>

      {/* === Código + link === */}
      <div className="panel">
        <h2>Tu código y link</h2>
        <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "12px 0", flexWrap: "wrap" }}>
          <div style={{ background: "#EEF3FB", border: "2px dashed #163680", padding: "14px 22px", borderRadius: 12, fontSize: 22, fontWeight: 800, letterSpacing: 1.5, color: "#163680" }} data-testid="referral-code">
            {data.referral_code}
          </div>
          <button onClick={() => copy(data.referral_code)} className="btn btn-secondary" data-testid="copy-code">
            <i className="fas fa-copy" /> Copiar código
          </button>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "10px 0", flexWrap: "wrap" }}>
          <input readOnly value={link} style={{ flex: 1, minWidth: 280, padding: 10, border: "1px solid #e7eaf2", borderRadius: 8, background: "#fafbfd", fontSize: 13 }} data-testid="referral-link" />
          <button onClick={() => copy(link)} className="btn btn-secondary" data-testid="copy-link">
            <i className="fas fa-copy" /> Copiar link
          </button>
          <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ background: "#25D366" }} data-testid="share-whatsapp">
            <i className="fab fa-whatsapp" /> Compartir por WhatsApp
          </a>
        </div>
      </div>

      {/* === Invitaciones enviadas === */}
      <div className="panel" data-testid="invitations-panel">
        <h2>Invitaciones enviadas {invitations?.total > 0 && <small style={{ fontSize: 13, color: "#9ca3af", fontWeight: 400 }}>({invitations.total})</small>}</h2>
        {!invitations || invitations.total === 0 ? (
          <p className="text-muted">Aún no has enviado invitaciones. Da clic en "Invitar amigos" arriba ↑</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Para</th><th>Email</th><th>Status</th><th>Enviado</th></tr>
            </thead>
            <tbody>
              {invitations.items.slice(0, 50).map((inv) => {
                const pill = STATUS_PILL[inv.status] || STATUS_PILL.sent;
                return (
                  <tr key={inv.id} data-testid={`inv-${inv.id}`}>
                    <td><strong>{inv.invitee_name || "—"}</strong></td>
                    <td><small>{inv.invitee_email}</small></td>
                    <td><span className={`sol-pill ${pill.cls}`}>{pill.label}</span></td>
                    <td><small>{fmtDate(inv.sent_at)}</small></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* === Referidos que se registraron === */}
      <div className="panel">
        <h2>Tus referidos confirmados</h2>
        {data.referidos.length === 0 ? (
          <p className="text-muted">Aún no tienes referidos registrados. Las invitaciones convertidas aparecerán aquí.</p>
        ) : (
          <table className="data-table">
            <thead><tr><th>Nombre</th><th>Email</th><th>Plan</th><th>Status</th><th>Tu recompensa</th><th>Fecha</th></tr></thead>
            <tbody>
              {data.referidos.map((r) => (
                <tr key={r.id} data-testid={`ref-${r.id}`}>
                  <td><strong>{r.nombre}</strong></td>
                  <td>{r.email}</td>
                  <td>{r.plan || "—"}</td>
                  <td><span className={`sol-pill sol-${r.membership_status === "active" ? "completada" : "pendiente"}`}>{r.membership_status || "—"}</span></td>
                  <td>{r.credited ? <span className="sol-pill sol-completada">+1 mes ✓</span> : <span className="sol-pill sol-pendiente">pendiente</span>}</td>
                  <td>{r.created_at ? new Date(r.created_at).toLocaleDateString("es-MX") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <InviteFriendsModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onSent={loadAll}
        referralLink={link}
      />
    </AppLayout>
  );
}
